import { Dialog } from "../ui/Dialog"
import { useTranslation } from "react-i18next"
import { Button } from "../ui/Button"
import { resetDialogs } from "@/store/dialogs-state"
import { Icon } from "../icons/Icon"
import { useUserQuery } from "@/api/users-api"
import { getOS } from "@/utils/system"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { getDesktopAppDownloadUrl } from "@/utils/polarr-urls"

const CHROME_DOWNLOAD_URL = "https://www.google.com/chrome/"

const openDesktopDownload = () => {
	const downloadUrl = getDesktopAppDownloadUrl(isPixiesetAuthEnabled())
	window.open(downloadUrl, "_blank", "noopener,noreferrer")
}

const openChromeDownload = () => {
	window.open(CHROME_DOWNLOAD_URL, "_blank", "noopener,noreferrer")
}

type SwitchToChromeDialogProps = {
	onDismiss?: () => void
	bodyText?: string
	trackingViewName?: string
}

export const SwitchToChromeDialog = ({
	onDismiss,
	bodyText,
	trackingViewName = "switch_to_chrome_dialog",
}: SwitchToChromeDialogProps) => {
	const { t } = useTranslation("components")
	const user = useUserQuery().data
	const trackingValues = {
		userId: user?.userId ?? null,
		OS: getOS(),
	}

	return (
		<Dialog
			trackingViewName={trackingViewName}
			contentSize="md-h-auto"
			disableClose
			contentProps={{ className: "rounded-[12px] p-6" }}
		>
			<div className="flex flex-col space-y-6">
				<div className="space-y-2">
					<div className="text-base font-medium leading-snug">
						{t("switchToChromeDialog.switchToChrome")}
					</div>
					<p className="text-sm font-medium leading-4 text-base-400">
						{bodyText ?? t("switchToChromeDialog.defaultBodyText")}
					</p>
				</div>
				<Button
					trackingLabel="open_desktop_app_download"
					aria-label={t("switchToChromeDialog.ariaDownloadDesktopApp")}
					trackingValues={trackingValues}
					variant="custom"
					size="custom"
					className="text-base w-full rounded-md border border-base-50/50 bg-base-700 p-4 font-semibold text-base-50 hover:bg-base-600"
					onClick={openDesktopDownload}
				>
					<div className="flex flex-row items-center justify-center gap-2">
						<Icon name="install_desktop" size={20} />
						<span>{t("switchToChromeDialog.installDesktopApp")}</span>
					</div>
				</Button>
				<Button
					trackingLabel="open_chrome_download"
					aria-label={t("switchToChromeDialog.ariaDownloadGoogleChrome")}
					trackingValues={trackingValues}
					variant="custom"
					size="custom"
					className="text-base w-full rounded-md border border-base-50/50 bg-base-700 p-4 font-semibold text-base-50 hover:bg-base-600"
					onClick={openChromeDownload}
				>
					<div className="flex flex-row items-center justify-center gap-2">
						<Icon name="download" size={20} />
						<span>{t("switchToChromeDialog.installGoogleChrome")}</span>
					</div>
				</Button>
				<Button
					trackingLabel="dismiss_switch_to_chrome_dialog"
					aria-label={t("switchToChromeDialog.ariaDismissBrowserWarning")}
					variant="primary"
					className="self-end px-3 py-2 text-md"
					onClick={() => {
						resetDialogs()
						onDismiss?.()
					}}
				>
					{t("switchToChromeDialog.gotIt")}
				</Button>
			</div>
		</Dialog>
	)
}
